import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsOption } from 'echarts';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { AlTableColumn, AlTableRowAction } from '../../../components/features/table/table.component';
import { SegmentOption } from '../../../components/segmented-control/segmented-control.component';
import { CreditService } from '../../../services/credit.service';
import { TokenAnalyticsService } from '../../../services/token-analytics.service';
import {
  OFFER_STATUSES,
  OfferStatus,
  OfferStatusEntry,
  PricingOffer,
  PricingOfferService,
} from '../../../services/pricing-offer.service';
import {
  OfferStatusDialogComponent,
  OfferStatusDialogResult,
  STATUS_TONE,
} from './offer-status-dialog/offer-status-dialog.component';
import {
  AiCostSource,
  AXIS_DEFAULTS,
  AxisField,
  AxisSpec,
  buildMatrix,
  computeOffer,
  computeUsageCases,
  CostAssumptions,
  DEFAULT_COSTS,
  defaultScenario,
  effectiveCosts,
  legacyToImport,
  MatrixMetric,
  MatrixResult,
  MatrixVariant,
  measureAiCost,
  MeasuredAiCost,
  newId,
  OfferResult,
  parseValues,
  readStoredCosts,
  Scenario,
  SLA_LEVELS,
  slaLevel,
  SlaLevelId,
  USAGE_CASES,
  USAGE_FIELD,
  UsageCase,
  usageCurve,
  usageOf,
  VariantResult,
  withField,
  withSlaLevel,
} from './pricing-model';

type Kind = 'text' | 'int' | 'eur' | 'eur3' | 'num' | 'pct' | 'mode' | 'bool' | 'case' | 'sla';

interface InputRow {
  key: keyof Scenario;
  kind: Kind;
  step?: number;
}

interface InputGroup {
  key: string;
  rows: InputRow[];
}

interface ResultRow {
  key: keyof VariantResult;
  kind: Kind;
  strong?: boolean;
}

interface Kpi {
  key: string;
  kind: Kind;
  managed: number;
  byok: number;
}

type CostField = Exclude<keyof CostAssumptions, 'llmSource'>;

/** Was am Angebot hängt, aber nicht gerechnet wird (Spalten der Tabelle `pricing_offers`). */
interface OfferMeta {
  customer: string;
  status: OfferStatus;
  status_reason: string | null;
  status_history: OfferStatusEntry[];
  hidden: boolean;
  created_by_name: string | null;
  updated_by_name: string | null;
  updated_at: string;
}

/** Zeile der Angebotsliste (app-table) — vorformatiert, in `recompute()` gebaut (Regel 18). */
interface OfferRow {
  id: string;
  name: string;
  customer: string;
  status: string;
  reason: string;
  learners: string;
  managed: string;
  byok: string;
  margins: string;
  compare: string;
  updated: string;
}

/** Bis 28.10.2026 lag der Rechner nur hier; beim ersten Öffnen wird er in die Datenbank übernommen. */
const LEGACY_STORAGE_KEY = 'al-pricing-calculator-v1';
/** Welche Spalte Basis ist — Ansicht dieses Browsers, nicht geteilt. */
const ACTIVE_KEY = 'al-pricing-calculator-active';
/** Höchstens so viele Angebote stehen gleichzeitig im Vergleich. */
const MAX_SCENARIOS = 8;
const SAVE_DELAY_MS = 700;
/** Zeitraum der gemessenen KI-Kosten (Standard der Karte auf Token-Analytics). */
const AI_COST_DAYS = 30;

/** Ab welcher Marge eine Zelle grün / gelb / rot getönt ist. */
const MARGIN_GOOD = 50;
const MARGIN_OK = 30;

/**
 * Angebote & Preise (super_admin): Angebote nebeneinander, jedes immer als
 * Managed (Credits bei uns) und BYOK gerechnet, dazu SLA, drei Nutzungsfälle,
 * Matrix und Nutzungskurve. Angebote, Status und Kostenannahmen liegen in der
 * Datenbank (al-user-management, `pricing_offers`) — alle super_admins sehen
 * dasselbe. Rechnung: pricing-model.ts.
 */
@Component({
  selector: 'app-admin-pricing',
  templateUrl: './pricing-calculator.component.html',
  styleUrl: './pricing-calculator.component.scss',
})
export class PricingCalculatorComponent implements OnInit, OnDestroy {
  /** Alle gespeicherten Angebote, auch ausgeblendete. */
  offers: Scenario[] = [];
  /** Angebote im Vergleich (nicht ausgeblendet) — die Spalten der Tabelle. */
  scenarios: Scenario[] = [];
  meta: Record<string, OfferMeta> = {};
  costs: CostAssumptions = { ...DEFAULT_COSTS };
  costsUpdatedBy: string | null = null;
  /** KI-Kosten aus dem Deckungsbeitrag; null = nicht lesbar oder zu wenig Verbrauch. */
  measuredAi: MeasuredAiCost | null = null;
  /** Annahmen, mit denen gerechnet wird — `costs` plus Messung. */
  effective: CostAssumptions = { ...DEFAULT_COSTS };
  activeId = '';

  loading = true;
  loadFailed = false;
  /** Laufende oder wartende Speichervorgänge. */
  pendingSaves = 0;
  busy = false;

  /** Je Szenario-ID das Ergebnis; neu berechnet in `recompute()` (Regel 18). */
  results: Record<string, OfferResult> = {};
  /** Je Szenario-ID alle drei Nutzungsfälle. */
  caseResults: Record<string, Record<UsageCase, OfferResult>> = {};
  /** Eingabezeilen, deren Wert sich zwischen den Szenarien unterscheidet. */
  differs: Partial<Record<keyof Scenario, boolean>> = {};

  readonly inputGroups: InputGroup[] = [
    {
      key: 'customer',
      rows: [
        { key: 'learners', kind: 'int', step: 500 },
        { key: 'teachers', kind: 'int', step: 100 },
      ],
    },
    {
      key: 'platform',
      rows: [
        { key: 'platformMode', kind: 'mode' },
        { key: 'platformPerLearner', kind: 'eur3', step: 0.01 },
        { key: 'discountPct', kind: 'pct', step: 1 },
      ],
    },
    {
      key: 'credits',
      rows: [
        { key: 'includedPerLearner', kind: 'num', step: 1 },
        { key: 'creditPrice', kind: 'eur3', step: 0.001 },
        { key: 'creditTiers', kind: 'bool' },
      ],
    },
    {
      key: 'usage',
      rows: [
        { key: 'usageCase', kind: 'case' },
        { key: 'usageLowPerLearner', kind: 'num', step: 0.5 },
        { key: 'usagePerLearner', kind: 'num', step: 0.5 },
        { key: 'usageHighPerLearner', kind: 'num', step: 0.5 },
      ],
    },
    {
      key: 'byok',
      rows: [
        { key: 'byokFactor', kind: 'num', step: 0.05 },
        { key: 'byokIncludedOps', kind: 'int', step: 10000 },
        { key: 'byokOverPer1000', kind: 'eur', step: 0.5 },
      ],
    },
    {
      key: 'sla',
      rows: [
        { key: 'slaLevel', kind: 'sla' },
        { key: 'slaFlatMonthly', kind: 'eur', step: 50 },
        { key: 'slaIncludedHours', kind: 'num', step: 1 },
        { key: 'slaHourlyRate', kind: 'eur', step: 5 },
        { key: 'slaExpectedHours', kind: 'num', step: 1 },
      ],
    },
    {
      key: 'oneOff',
      rows: [
        { key: 'setupFee', kind: 'eur', step: 500 },
        { key: 'pilotFee', kind: 'eur', step: 500 },
      ],
    },
  ];

  readonly resultRows: ResultRow[] = [
    { key: 'baseFee', kind: 'eur' },
    { key: 'overCharge', kind: 'eur' },
    { key: 'slaCharge', kind: 'eur' },
    { key: 'monthly', kind: 'eur', strong: true },
    { key: 'annual', kind: 'eur', strong: true },
    { key: 'year1', kind: 'eur' },
    { key: 'perLearnerMonth', kind: 'eur3' },
    { key: 'perLearnerYear', kind: 'eur' },
    { key: 'aiCost', kind: 'eur' },
    { key: 'costMonthly', kind: 'eur' },
    { key: 'contributionAnnual', kind: 'eur' },
    { key: 'marginPct', kind: 'pct', strong: true },
  ];

  readonly variants: Array<'managed' | 'byok'> = ['managed', 'byok'];
  /** Zeilen der Nutzungsfall-Übersicht je Variante. */
  readonly caseRowKeys = ['monthly', 'overCharge', 'aiCost', 'costMonthly', 'contributionMonthly', 'marginPct'] as const;

  readonly costFields: Array<{ key: CostField; kind: Kind; step: number }> = [
    { key: 'llmPerCredit', kind: 'eur3', step: 0.001 },
    { key: 'supportPerLearner', kind: 'eur3', step: 0.005 },
    { key: 'opsPerCredit', kind: 'num', step: 0.1 },
    { key: 'byokResidualPerOp', kind: 'eur3', step: 0.0005 },
    { key: 'listPlatformPerLearner', kind: 'eur3', step: 0.01 },
    { key: 'listCreditPrice', kind: 'eur3', step: 0.001 },
    { key: 'slaCostPerHour', kind: 'eur', step: 5 },
  ];

  readonly axisFields: AxisField[] = [
    'learners',
    'platformPerLearner',
    'includedPerLearner',
    'usagePerLearner',
    'creditPrice',
    'byokFactor',
    'discountPct',
  ];

  readonly maxScenarios = MAX_SCENARIOS;
  readonly usageCases = USAGE_CASES;
  readonly usageField = USAGE_FIELD;
  readonly slaLevels = SLA_LEVELS;
  readonly statuses = OFFER_STATUSES;

  modeOptions: SegmentOption[] = [];
  caseOptions: SegmentOption[] = [];
  aiSourceOptions: SegmentOption[] = [];
  statusFilterOptions: SegmentOption[] = [];
  metricOptions: SegmentOption[] = [];
  variantOptions: SegmentOption[] = [];

  // Matrix
  rowAxis: AxisSpec = { field: 'platformPerLearner', ...AXIS_DEFAULTS.platformPerLearner };
  colAxis: AxisSpec = { field: 'usagePerLearner', ...AXIS_DEFAULTS.usagePerLearner };
  matrixMetric: MatrixMetric = 'annual';
  matrixVariant: MatrixVariant = 'managed';
  matrix: MatrixResult = { rowValues: [], colValues: [], cells: [] };

  // Varianten erzeugen
  variantField: AxisField = 'learners';
  variantValues = '';

  chartOption: EChartsOption = {};

  /** Kacheln über der Tabelle: Basis-Szenario, Managed und BYOK nebeneinander. */
  kpis: Kpi[] = [];

  // Angebotsliste
  statusFilter: 'all' | OfferStatus = 'all';
  offerRows: OfferRow[] = [];
  offerColumns: AlTableColumn[] = [];
  offerActions: AlTableRowAction[] = [];
  statusCounts: Record<string, number> = {};

  private readonly nf = new Map<string, Intl.NumberFormat>();
  /** Je Angebot eine Kette: Speichern und Statuswechsel laufen nacheinander. */
  private chains = new Map<string, Promise<void>>();
  private dirty = new Set<string>();
  private queued = new Set<string>();
  private saveTimer?: ReturnType<typeof setTimeout>;
  private costsTimer?: ReturnType<typeof setTimeout>;
  private langSub?: { unsubscribe(): void };

  constructor(
    private t: TranslateService,
    private toastr: NbToastrService,
    private creditService: CreditService,
    private offerService: PricingOfferService,
    private tokenAnalytics: TokenAnalyticsService,
    private dialog: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.buildOptions();
    this.langSub = this.t.onLangChange.subscribe(() => {
      this.buildOptions();
      this.recompute();
    });
    try {
      this.activeId = localStorage.getItem(ACTIVE_KEY) ?? '';
    } catch {
      this.activeId = '';
    }
    void this.reload(true);
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    // Was noch wartet, sofort schicken statt zu verlieren.
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.flushSaves();
    }
    if (this.costsTimer) {
      clearTimeout(this.costsTimer);
      this.saveCosts();
    }
  }

  /** Zurück auf den Tab: Änderungen anderer super_admins holen, wenn hier nichts offen ist. */
  @HostListener('window:focus')
  onWindowFocus(): void {
    if (!this.loading && !this.pendingSaves && !this.dirty.size && !this.costsTimer) void this.reload(false);
  }

  // ── Laden ───────────────────────────────────────────────────────────

  /** `measure`: Messung neu holen — der Bericht ist teuer, also nicht bei jedem Tab-Wechsel. */
  async reload(first: boolean, measure = first): Promise<void> {
    if (first) this.loading = true;
    try {
      const [state] = await Promise.all([firstValueFrom(this.offerService.list()), measure ? this.loadMeasured() : null]);
      const stored = state.settings['costs'];
      if (stored?.value) {
        this.costs = readStoredCosts(stored.value as Partial<CostAssumptions>);
        this.costsUpdatedBy = stored.updated_by_name;
      } else if (first) {
        await this.loadListPrice();
      }
      this.offers = state.offers.map((o) => this.toScenario(o));
      this.meta = Object.fromEntries(state.offers.map((o) => [o.id, this.metaOf(o)]));
      this.loadFailed = false;
      if (first) await this.importLegacy();
      if (first && !this.offers.length) await this.seed();
    } catch {
      if (first) this.loadFailed = true;
      else this.toastr.danger(this.t.instant('pages.admin.pricing.offers.loadFailed'));
    } finally {
      this.loading = false;
      this.recompute();
    }
  }

  /** KI-Kosten je Credit aus dem Deckungsbeitrag. Ohne sie rechnet die Seite mit dem eigenen Wert. */
  private async loadMeasured(): Promise<void> {
    try {
      this.measuredAi = measureAiCost(await firstValueFrom(this.tokenAnalytics.getMargin(AI_COST_DAYS)));
    } catch {
      this.measuredAi = null;
    }
  }

  /**
   * Szenarien, die noch nur in diesem Browser liegen, in die Datenbank holen —
   * auch wenn dort schon Angebote stehen: jeder super_admin hatte seinen eigenen
   * Stand. Ein Fehlschlag lässt den Browser-Stand liegen, er wird nie verworfen.
   */
  private async importLegacy(): Promise<void> {
    const legacy = this.readLegacy();
    if (!legacy.length) return;
    const fresh = legacyToImport(legacy, [...this.offers, ...this.presetScenarios('enterprise')]);
    let visible = this.offers.filter((s) => !this.meta[s.id]?.hidden).length;
    let imported = 0;
    for (const s of fresh) {
      const hidden = visible >= MAX_SCENARIOS;
      if (!(await this.createOffer(s, hidden))) return;
      imported++;
      if (!hidden) visible++;
    }
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Beim nächsten Öffnen erkennt legacyToImport die Szenarien als gespeichert.
    }
    if (imported) this.toastr.success(this.t.instant('pages.admin.pricing.offers.imported', { count: imported }));
  }

  /** Leere Datenbank: die Vorlage 8.000 / 14.000 als Startpunkt. */
  private async seed(): Promise<void> {
    for (const s of this.presetScenarios('enterprise')) await this.createOffer(s);
  }

  private async loadListPrice(): Promise<void> {
    try {
      const p = await firstValueFrom(this.creditService.getPricing());
      if (p?.credits_per_eur > 0) {
        const price = Math.round((1 / p.credits_per_eur) * 10000) / 10000;
        this.costs = { ...this.costs, listCreditPrice: price };
      }
    } catch {
      // Listenpreis bleibt der Standardwert.
    }
  }

  private toScenario(o: PricingOffer): Scenario {
    return defaultScenario({ ...(o.scenario as Partial<Scenario>), id: o.id, name: o.name });
  }

  private metaOf(o: PricingOffer): OfferMeta {
    return {
      customer: o.customer ?? '',
      status: o.status,
      status_reason: o.status_reason,
      status_history: o.status_history ?? [],
      hidden: o.hidden,
      created_by_name: o.created_by_name,
      updated_by_name: o.updated_by_name,
      updated_at: o.updated_at,
    };
  }

  private payload(s: Scenario): Record<string, unknown> {
    const { id: _id, name: _name, ...rest } = s;
    return rest;
  }

  // ── Speichern ───────────────────────────────────────────────────────

  get saving(): boolean {
    return this.pendingSaves > 0 || this.dirty.size > 0;
  }

  /** Läuft nach allem, was für dieses Angebot schon ansteht. */
  private enqueue(id: string, op: () => Promise<void>): Promise<void> {
    this.pendingSaves++;
    const next = (this.chains.get(id) ?? Promise.resolve())
      .then(op)
      .catch(() => undefined)
      .finally(() => {
        this.pendingSaves--;
      });
    this.chains.set(id, next);
    return next;
  }

  private scheduleSave(ids: string[]): void {
    ids.forEach((id) => this.dirty.add(id));
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.flushSaves(), SAVE_DELAY_MS);
  }

  private flushSaves(): void {
    this.saveTimer = undefined;
    for (const id of this.dirty) {
      if (this.queued.has(id)) continue;
      this.queued.add(id);
      void this.enqueue(id, () => {
        this.queued.delete(id);
        return this.saveNow(id);
      });
    }
    this.dirty.clear();
  }

  /** Ganzer Stand eines Angebots — mit dem Stand, auf dem er beruht. */
  private async saveNow(id: string): Promise<void> {
    const s = this.offers.find((x) => x.id === id);
    const m = this.meta[id];
    if (!s || !m) return;
    try {
      const o = await firstValueFrom(
        this.offerService.update(id, {
          name: s.name.trim() || '—',
          customer: m.customer,
          hidden: m.hidden,
          scenario: this.payload(s),
          expected_updated_at: m.updated_at,
        }),
      );
      this.applyServerMeta(o);
    } catch (err) {
      this.handleSaveError(id, err);
    }
  }

  /** Nur was der Server führt — lokale Eingaben seit dem Absenden bleiben. */
  private applyServerMeta(o: PricingOffer): void {
    const m = this.meta[o.id];
    if (!m) return;
    Object.assign(m, {
      status: o.status,
      status_reason: o.status_reason,
      status_history: o.status_history ?? [],
      updated_by_name: o.updated_by_name,
      updated_at: o.updated_at,
    });
    this.recomputeList();
  }

  private handleSaveError(id: string, err: unknown): void {
    const e = err as HttpErrorResponse;
    const fresh = e?.status === 409 ? (e.error?.detail?.offer as PricingOffer | undefined) : undefined;
    if (fresh) {
      // Jemand anderes war schneller: seinen Stand zeigen statt ihn zu überschreiben.
      const i = this.offers.findIndex((x) => x.id === id);
      if (i >= 0) this.offers[i] = this.toScenario(fresh);
      this.meta[id] = this.metaOf(fresh);
      this.dirty.delete(id);
      this.recompute();
      this.toastr.warning(
        this.t.instant('pages.admin.pricing.offers.conflict', { name: fresh.name, by: fresh.updated_by_name || '—' }),
      );
      return;
    }
    if (e?.status === 404) {
      this.offers = this.offers.filter((x) => x.id !== id);
      delete this.meta[id];
      this.recompute();
    }
    this.toastr.danger(this.t.instant('pages.admin.pricing.offers.saveFailed'));
  }

  private async createOffer(s: Scenario, hidden = false): Promise<Scenario | null> {
    try {
      const o = await firstValueFrom(
        this.offerService.create({
          name: s.name.trim() || '—',
          customer: this.meta[s.id]?.customer || null,
          scenario: this.payload(s),
          hidden,
        }),
      );
      const created = this.toScenario(o);
      this.offers = [...this.offers, created];
      this.meta[o.id] = this.metaOf(o);
      return created;
    } catch {
      this.toastr.danger(this.t.instant('pages.admin.pricing.offers.saveFailed'));
      return null;
    }
  }

  private saveCosts(): void {
    this.costsTimer = undefined;
    this.offerService.putSetting('costs', { ...this.costs }).subscribe({
      error: () => this.toastr.danger(this.t.instant('pages.admin.pricing.offers.saveFailed')),
    });
  }

  // ── Szenarien ───────────────────────────────────────────────────────

  get active(): Scenario | undefined {
    return this.scenarios.find((s) => s.id === this.activeId);
  }

  get canAdd(): boolean {
    return this.scenarios.length < MAX_SCENARIOS;
  }

  trackScenario(_i: number, s: Scenario): string {
    return s.id;
  }

  select(s: Scenario): void {
    if (this.activeId === s.id) return;
    this.activeId = s.id;
    this.rememberActive();
    this.recompute();
  }

  update(s: Scenario, key: keyof Scenario, value: unknown): void {
    const row = this.inputGroups.flatMap((g) => g.rows).find((r) => r.key === key);
    if (row?.kind === 'sla') {
      Object.assign(s, withSlaLevel(s, value as SlaLevelId));
      this.changed([s]);
      return;
    }
    let next: unknown = value;
    if (row && !['text', 'mode', 'bool', 'case'].includes(row.kind)) {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return;
      next = Math.max(0, n);
    }
    Object.assign(s, { [key]: next });
    this.changed([s]);
  }

  rename(s: Scenario, name: string): void {
    s.name = name;
    this.changed([s]);
  }

  setCustomer(s: Scenario, customer: string): void {
    const m = this.meta[s.id];
    if (!m) return;
    m.customer = customer;
    this.changed([s]);
  }

  /** Nutzungsfall des Basis-Angebots (Kacheln, Matrix, Kurve). */
  setActiveCase(c: string): void {
    const s = this.active;
    if (s) this.update(s, 'usageCase', c as UsageCase);
  }

  async addScenario(): Promise<void> {
    if (!this.canAdd) return;
    const base = this.active ?? defaultScenario({ creditPrice: this.costs.listCreditPrice });
    const created = await this.createOffer({
      ...base,
      name: this.t.instant('pages.admin.pricing.scenarios.copyName', { name: base.name }),
    });
    if (created) this.activate(created);
  }

  async duplicate(s: Scenario): Promise<void> {
    if (!this.canAdd) return;
    const created = await this.createOffer({
      ...s,
      name: this.t.instant('pages.admin.pricing.scenarios.copyName', { name: s.name }),
    });
    if (created) this.activate(created);
  }

  remove(s: Scenario): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.admin.pricing.offers.deleteTitle',
          message: this.t.instant('pages.admin.pricing.offers.deleteMessage', { name: s.name }),
          confirmLabel: 'buttons.delete',
        },
      })
      .onClose.subscribe((ok) => {
        if (!ok) return;
        this.dirty.delete(s.id);
        void this.enqueue(s.id, async () => {
          try {
            await firstValueFrom(this.offerService.remove(s.id));
            this.offers = this.offers.filter((x) => x.id !== s.id);
            delete this.meta[s.id];
            this.recompute();
          } catch {
            this.toastr.danger(this.t.instant('pages.admin.pricing.offers.saveFailed'));
          }
        });
      });
  }

  /** Zeilenmenü der Angebotsliste. `app-table` ruft keine Aktion selbst auf, es meldet sie. */
  onOfferTableEvent(payload: { event: string; data: any }): void {
    if (payload.event !== 'rowAction') return;
    const id = (payload.data?.row as OfferRow | undefined)?.id;
    if (!id) return;
    const key = payload.data?.action?.key;
    if (key === 'load') this.setHidden(id, false);
    if (key === 'hide') this.setHidden(id, true);
    if (key === 'status') this.openStatus(id);
    if (key === 'delete') {
      const s = this.offers.find((x) => x.id === id);
      if (s) this.remove(s);
    }
  }

  /** Aus dem Vergleich nehmen (bleibt in der Liste) bzw. wieder laden. */
  setHidden(id: string, hidden: boolean): void {
    const m = this.meta[id];
    const s = this.offers.find((x) => x.id === id);
    if (!m || !s || m.hidden === hidden) return;
    if (!hidden && !this.canAdd) {
      this.toastr.warning(this.t.instant('pages.admin.pricing.variants.full', { max: MAX_SCENARIOS }));
      return;
    }
    m.hidden = hidden;
    if (!hidden) this.activeId = id;
    this.rememberActive();
    this.changed([s]);
  }

  openStatus(id: string): void {
    const s = this.offers.find((x) => x.id === id);
    const m = this.meta[id];
    if (!s || !m) return;
    this.dialog
      .open(OfferStatusDialogComponent, {
        context: { name: s.name, status: m.status, reason: m.status_reason ?? '', history: m.status_history },
      })
      .onClose.subscribe((r?: OfferStatusDialogResult) => {
        if (!r) return;
        // Erst wartende Eingaben schicken, dann der Statuswechsel — dieselbe Kette.
        if (this.dirty.has(id)) this.flushSaves();
        void this.enqueue(id, async () => {
          try {
            const o =
              r.status === m.status
                ? await firstValueFrom(this.offerService.update(id, { status_reason: r.reason }))
                : await firstValueFrom(this.offerService.setStatus(id, r.status, r.reason));
            this.applyServerMeta(o);
          } catch {
            this.toastr.danger(this.t.instant('pages.admin.pricing.offers.saveFailed'));
          }
        });
      });
  }

  /** Wert des aktiven Szenarios in alle Szenarien übernehmen. */
  applyToAll(key: keyof Scenario): void {
    const src = this.active;
    if (!src) return;
    // Die SLA-Stufe nimmt ihre Werte mit — sonst stünde „Premium" über fremden Preisen.
    const keys: Array<keyof Scenario> =
      key === 'slaLevel' ? ['slaLevel', 'slaFlatMonthly', 'slaIncludedHours', 'slaHourlyRate', 'slaExpectedHours'] : [key];
    for (const s of this.scenarios) for (const k of keys) Object.assign(s, { [k]: src[k] });
    this.changed(this.scenarios);
  }

  /** Vorlage: nimmt die Angebote im Vergleich heraus (gelöscht wird nichts) und legt die Vorlage neu an. */
  async applyPreset(preset: 'enterprise' | 'list' | 'empty'): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const visible = this.scenarios;
      for (const s of visible) this.meta[s.id].hidden = true;
      if (visible.length) this.changed(visible);
      let first: Scenario | null = null;
      for (const s of this.presetScenarios(preset)) {
        const created = await this.createOffer(s);
        first = first ?? created;
      }
      if (first) this.activate(first);
    } finally {
      this.busy = false;
    }
  }

  private presetScenarios(preset: 'enterprise' | 'list' | 'empty'): Scenario[] {
    const price = this.costs.listCreditPrice;
    if (preset === 'enterprise') {
      return [
        defaultScenario({ name: this.t.instant('pages.admin.pricing.presets.nameA'), creditPrice: price }),
        defaultScenario({
          name: this.t.instant('pages.admin.pricing.presets.nameB'),
          learners: 14000,
          teachers: 2500,
          platformPerLearner: 0.26,
          byokIncludedOps: 175000,
          setupFee: 14500,
          creditPrice: price,
        }),
      ];
    }
    if (preset === 'list') {
      // Basic/Pro: 0,59 € bzw. 0,596 € Plattform + 10 Credits je Lernendem = 99 € / 249 €.
      const list = {
        includedPerLearner: 10,
        usagePerLearner: 10,
        usageLowPerLearner: 5,
        usageHighPerLearner: 15,
        creditPrice: price,
        setupFee: 0,
        byokIncludedOps: 0,
      };
      return [
        defaultScenario({ ...list, name: 'Basic', learners: 100, teachers: 5, platformPerLearner: 0.59 }),
        defaultScenario({ ...list, name: 'Pro', learners: 250, teachers: 10, platformPerLearner: 0.596 }),
      ];
    }
    return [defaultScenario({ name: this.t.instant('pages.admin.pricing.presets.nameNew'), creditPrice: price })];
  }

  async generateVariants(): Promise<void> {
    const src = this.active;
    const values = parseValues(this.variantValues);
    if (!src || !values.length || this.busy) return;
    const room = MAX_SCENARIOS - this.scenarios.length;
    if (room <= 0 || values.length > room) {
      this.toastr.warning(this.t.instant('pages.admin.pricing.variants.full', { max: MAX_SCENARIOS }));
      if (room <= 0) return;
    }
    this.busy = true;
    try {
      for (const v of values.slice(0, room)) {
        await this.createOffer({ ...withField(src, this.variantField, v), name: this.variantName(this.variantField, v) });
      }
      this.variantValues = '';
      this.recompute();
    } finally {
      this.busy = false;
    }
  }

  /** Matrixzelle als neues Szenario übernehmen. */
  async takeCell(ri: number, ci: number): Promise<void> {
    const src = this.active;
    if (!src || !this.canAdd) {
      if (!this.canAdd) this.toastr.warning(this.t.instant('pages.admin.pricing.variants.full', { max: MAX_SCENARIOS }));
      return;
    }
    const rv = this.matrix.rowValues[ri];
    const cv = this.matrix.colValues[ci];
    const s = withField(withField(src, this.rowAxis.field, rv), this.colAxis.field, cv);
    const created = await this.createOffer({
      ...s,
      name: `${this.variantName(this.rowAxis.field, rv)} · ${this.variantName(this.colAxis.field, cv)}`,
    });
    if (!created) return;
    this.activate(created);
    this.toastr.success(this.t.instant('pages.admin.pricing.matrix.taken', { name: created.name }));
  }

  private activate(s: Scenario): void {
    this.activeId = s.id;
    this.rememberActive();
    this.recompute();
  }

  private rememberActive(): void {
    try {
      localStorage.setItem(ACTIVE_KEY, this.activeId);
    } catch {
      // Ansicht dieses Browsers — ohne Speicher ist die erste Spalte Basis.
    }
  }

  // ── Kosten & Matrix ─────────────────────────────────────────────────

  setAiSource(v: string): void {
    this.costs = { ...this.costs, llmSource: v as AiCostSource };
    this.costsChanged();
  }

  /** Gilt die Messung gerade? Dann ist das Feld „KI-Kosten je Credit" nur Anzeige. */
  get aiCostMeasured(): boolean {
    return this.costs.llmSource === 'measured' && !!this.measuredAi;
  }

  updateCost(key: CostField, value: unknown): void {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return;
    this.costs = { ...this.costs, [key]: Math.max(0, n) };
    this.costsChanged();
  }

  resetCosts(): void {
    this.costs = { ...DEFAULT_COSTS, listCreditPrice: this.costs.listCreditPrice };
    this.costsChanged();
  }

  private costsChanged(): void {
    this.recompute();
    if (this.costsTimer) clearTimeout(this.costsTimer);
    this.costsTimer = setTimeout(() => this.saveCosts(), SAVE_DELAY_MS);
  }

  setStatusFilter(v: string): void {
    this.statusFilter = v as 'all' | OfferStatus;
    this.recomputeList();
  }

  statusTone(s: OfferStatus): string {
    return STATUS_TONE[s];
  }

  slaInfo(id: SlaLevelId): string {
    const l = slaLevel(id);
    if (l.id === 'none') return '';
    return this.t.instant('pages.admin.pricing.sla.info', {
      critical: l.responseCriticalH,
      normal: l.responseNormalH,
      channels: this.t.instant(`pages.admin.pricing.sla.levels.${l.id}.channels`),
      hours: this.t.instant(`pages.admin.pricing.sla.levels.${l.id}.hours`),
    });
  }

  setAxisField(axis: 'row' | 'col', field: AxisField): void {
    const spec = { field, ...AXIS_DEFAULTS[field] };
    if (axis === 'row') this.rowAxis = spec;
    else this.colAxis = spec;
    this.recompute();
  }

  updateAxis(axis: 'row' | 'col', key: 'start' | 'step' | 'count', value: unknown): void {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return;
    const spec = axis === 'row' ? this.rowAxis : this.colAxis;
    const next = { ...spec, [key]: key === 'count' ? Math.max(1, Math.min(20, Math.round(n))) : n };
    if (axis === 'row') this.rowAxis = next;
    else this.colAxis = next;
    this.recompute();
  }

  setMetric(v: string): void {
    this.matrixMetric = v as MatrixMetric;
    this.recompute();
  }

  setVariant(v: string): void {
    this.matrixVariant = v as MatrixVariant;
    this.recompute();
  }

  // ── Rechnen ─────────────────────────────────────────────────────────

  private changed(dirty: Scenario[]): void {
    this.recompute();
    this.scheduleSave(dirty.map((s) => s.id));
  }

  private recompute(): void {
    this.effective = effectiveCosts(this.costs, this.measuredAi);
    this.scenarios = this.offers.filter((s) => !this.meta[s.id]?.hidden);
    if (!this.scenarios.some((s) => s.id === this.activeId)) {
      this.activeId = this.scenarios[0]?.id ?? '';
    }
    const results: Record<string, OfferResult> = {};
    const caseResults: Record<string, Record<UsageCase, OfferResult>> = {};
    for (const s of this.offers) {
      caseResults[s.id] = computeUsageCases(s, this.effective);
      results[s.id] = caseResults[s.id][s.usageCase] ?? computeOffer(s, this.effective);
    }
    this.results = results;
    this.caseResults = caseResults;

    const differs: Partial<Record<keyof Scenario, boolean>> = {};
    for (const g of this.inputGroups) {
      for (const r of g.rows) {
        differs[r.key] = new Set(this.scenarios.map((s) => s[r.key])).size > 1;
      }
    }
    this.differs = differs;

    const base = this.active;
    const r = base ? results[base.id] : undefined;
    this.kpis = r
      ? [
          { key: 'revenueMonth', kind: 'eur', managed: r.managed.monthly, byok: r.byok.monthly },
          { key: 'profitMonth', kind: 'eur', managed: r.managed.contributionMonthly, byok: r.byok.contributionMonthly },
          { key: 'revenueYear', kind: 'eur', managed: r.managed.annual, byok: r.byok.annual },
          { key: 'profitYear', kind: 'eur', managed: r.managed.contributionAnnual, byok: r.byok.contributionAnnual },
          { key: 'margin', kind: 'pct', managed: r.managed.marginPct, byok: r.byok.marginPct },
        ]
      : [];
    if (base) {
      this.matrix = buildMatrix(base, this.effective, this.rowAxis, this.colAxis, this.matrixMetric, this.matrixVariant);
      this.chartOption = this.buildChart(base);
    }
    this.recomputeList();
  }

  /** Angebotsliste — neu gebaut nur bei Änderungen, nie aus einem Getter (Regel 18). */
  private recomputeList(): void {
    const counts: Record<string, number> = { all: this.offers.length };
    for (const st of OFFER_STATUSES) counts[st] = 0;
    for (const s of this.offers) {
      const st = this.meta[s.id]?.status;
      if (st) counts[st]++;
    }
    this.statusCounts = counts;
    this.buildStatusFilterOptions();
    const p = 'pages.admin.pricing.offers';
    this.offerRows = this.offers
      .filter((s) => this.statusFilter === 'all' || this.meta[s.id]?.status === this.statusFilter)
      .map((s) => {
        const m = this.meta[s.id];
        const c = this.caseResults[s.id];
        const r = this.results[s.id];
        return {
          id: s.id,
          name: s.name,
          customer: m?.customer || '—',
          status: m ? this.t.instant(`${p}.status.${m.status}`) : '',
          reason: m?.status_reason || '—',
          learners: this.format(s.learners, 'int'),
          managed: r ? this.format(r.managed.monthly, 'eur') : '–',
          byok: r ? this.format(r.byok.monthly, 'eur') : '–',
          margins: c
            ? USAGE_CASES.map((k) => this.format(c[k].managed.marginPct, 'pct')).join(' · ') +
              ' / ' +
              USAGE_CASES.map((k) => this.format(c[k].byok.marginPct, 'pct')).join(' · ')
            : '–',
          compare: m?.hidden ? this.t.instant(`${p}.hiddenLabel`) : this.t.instant(`${p}.inCompare`),
          updated: m ? `${new Date(m.updated_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} · ${m.updated_by_name || '—'}` : '',
        };
      });
  }

  private buildChart(s: Scenario): EChartsOption {
    const points = usageCurve(s, this.effective);
    const label = (k: string) => this.t.instant(`pages.admin.pricing.chart.${k}`);
    const eur = (v: number) => this.format(v, 'eur');
    const line = (name: string, data: number[], color: string, dashed = false) => ({
      name,
      type: 'line' as const,
      smooth: false,
      showSymbol: false,
      data: points.map((p, i) => [p.usage, data[i]]),
      lineStyle: { width: 2, color, type: dashed ? ('dashed' as const) : ('solid' as const) },
      itemStyle: { color },
    });
    const managedRev = line(label('managedRevenue'), points.map((p) => p.managedRevenue), '#2563EB');
    (managedRev as any).markLine = {
      symbol: 'none',
      silent: true,
      lineStyle: { color: '#526581', type: 'dotted' },
      label: { color: '#526581' },
      data: USAGE_CASES.map((c) => ({
        xAxis: usageOf(s, c),
        label: { formatter: this.t.instant(`pages.admin.pricing.usage.cases.${c}`) },
        lineStyle: { type: c === s.usageCase ? 'solid' : 'dotted' },
      })),
    };
    return {
      grid: { left: 70, right: 24, top: 40, bottom: 48 },
      legend: { top: 0, textStyle: { color: '#526581' } },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: any) => eur(Number(v)),
      },
      xAxis: {
        type: 'value',
        name: label('xAxis'),
        nameLocation: 'middle',
        nameGap: 28,
        min: 0,
        max: 15,
        axisLabel: { color: '#526581' },
        nameTextStyle: { color: '#526581' },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#526581', formatter: (v: number) => this.format(v, 'eurShort') },
      },
      series: [
        managedRev,
        line(label('managedCost'), points.map((p) => p.managedCost), '#2563EB', true),
        line(label('byokRevenue'), points.map((p) => p.byokRevenue), '#00A35C'),
        line(label('byokCost'), points.map((p) => p.byokCost), '#00A35C', true),
      ],
    } as EChartsOption;
  }

  // ── Anzeige ─────────────────────────────────────────────────────────

  format(value: number, kind: Kind | 'eurShort'): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '–';
    const fmt = (key: string, opts: Intl.NumberFormatOptions) => {
      let f = this.nf.get(key);
      if (!f) {
        f = new Intl.NumberFormat('de-DE', opts);
        this.nf.set(key, f);
      }
      return f.format(value);
    };
    switch (kind) {
      case 'eur':
        return fmt('eur', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
      case 'eurShort':
        if (Math.abs(value) < 10000) return fmt('eur', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
        value = value / 1000;
        return `${fmt('int', { maximumFractionDigits: 0 })} k€`;
      case 'eur3':
        return fmt('eur3', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 3 });
      case 'pct':
        return `${fmt('pct', { maximumFractionDigits: 0 })} %`;
      case 'int':
        return fmt('int', { maximumFractionDigits: 0 });
      default:
        return fmt('num', { maximumFractionDigits: 2 });
    }
  }

  /** Matrixwert in der Einheit der Kennzahl. */
  formatMetric(value: number): string {
    const kind: Kind = this.matrixMetric === 'marginPct' ? 'pct' : this.matrixMetric === 'perLearnerMonth' ? 'eur3' : 'eur';
    const text = this.format(value, kind);
    return this.matrixVariant === 'diff' && value > 0 ? `+${text}` : text;
  }

  formatAxis(field: AxisField, value: number): string {
    return this.format(value, this.axisKind(field));
  }

  marginTone(pct: number): string {
    if (pct >= MARGIN_GOOD) return 'accept';
    if (pct >= MARGIN_OK) return 'info';
    if (pct >= 0) return 'warning';
    return 'error';
  }

  cellTone(value: number, margin: number): string {
    if (this.matrixVariant === 'diff') return value > 0 ? 'accept' : value < 0 ? 'error' : 'neutral';
    return this.marginTone(margin);
  }

  private axisKind(field: AxisField): Kind {
    switch (field) {
      case 'learners':
        return 'int';
      case 'platformPerLearner':
      case 'creditPrice':
        return 'eur3';
      case 'discountPct':
        return 'pct';
      default:
        return 'num';
    }
  }

  private variantName(field: AxisField, value: number): string {
    return this.t.instant(`pages.admin.pricing.variants.names.${field}`, { value: this.formatAxis(field, value) });
  }

  private buildOptions(): void {
    const p = 'pages.admin.pricing';
    this.modeOptions = [
      { value: 'fixed', label: this.t.instant(`${p}.mode.fixed`) },
      { value: 'tiered', label: this.t.instant(`${p}.mode.tiered`) },
    ];
    this.caseOptions = USAGE_CASES.map((c) => ({ value: c, label: this.t.instant(`${p}.usage.cases.${c}`) }));
    this.aiSourceOptions = (['measured', 'manual'] as AiCostSource[]).map((v) => ({
      value: v,
      label: this.t.instant(`${p}.costs.aiSource.${v}`),
    }));
    this.buildStatusFilterOptions();
    const o = `${p}.offers`;
    this.offerColumns = [
      { key: 'name', title: this.t.instant(`${o}.columns.name`), kind: 'name' },
      { key: 'customer', title: this.t.instant(`${o}.columns.customer`) },
      { key: 'status', title: this.t.instant(`${o}.columns.status`) },
      { key: 'reason', title: this.t.instant(`${o}.columns.reason`) },
      { key: 'learners', title: this.t.instant(`${o}.columns.learners`), kind: 'number' },
      { key: 'managed', title: this.t.instant(`${o}.columns.managed`), kind: 'number' },
      { key: 'byok', title: this.t.instant(`${o}.columns.byok`), kind: 'number' },
      { key: 'margins', title: this.t.instant(`${o}.columns.margins`), kind: 'number' },
      { key: 'compare', title: this.t.instant(`${o}.columns.compare`) },
      { key: 'updated', title: this.t.instant(`${o}.columns.updated`), kind: 'date' },
    ];
    this.offerActions = [
      {
        key: 'load',
        title: this.t.instant(`${o}.actions.load`),
        icon: 'log-in-outline',
        disabled: (row: OfferRow) => !this.meta[row.id]?.hidden,
      },
      {
        key: 'hide',
        title: this.t.instant(`${o}.actions.hide`),
        icon: 'eye-off-outline',
        disabled: (row: OfferRow) => !!this.meta[row.id]?.hidden,
      },
      {
        key: 'status',
        title: this.t.instant(`${o}.actions.status`),
        icon: 'flag-outline',
      },
      {
        key: 'delete',
        title: this.t.instant('buttons.delete'),
        icon: 'trash-2-outline',
      },
    ];
    this.metricOptions = (['monthly', 'annual', 'perLearnerMonth', 'marginPct', 'contributionAnnual'] as MatrixMetric[]).map(
      (m) => ({ value: m, label: this.t.instant(`${p}.matrix.metrics.${m}`) }),
    );
    this.variantOptions = (['managed', 'byok', 'diff'] as MatrixVariant[]).map((v) => ({
      value: v,
      label: this.t.instant(`${p}.matrix.variants.${v}`),
    }));
  }

  private buildStatusFilterOptions(): void {
    const p = 'pages.admin.pricing.offers';
    const n = (k: string) => this.statusCounts[k] ?? 0;
    this.statusFilterOptions = [
      { value: 'all', label: `${this.t.instant(`${p}.filterAll`)} (${n('all')})` },
      ...OFFER_STATUSES.map((st) => ({ value: st, label: `${this.t.instant(`${p}.status.${st}`)} (${n(st)})` })),
    ];
  }

  // ── Export ──────────────────────────────────────────────────────────

  async copyText(): Promise<void> {
    const p = 'pages.admin.pricing.export';
    const lines: string[] = [];
    for (const s of this.scenarios) {
      const r = this.results[s.id];
      if (!r) continue;
      lines.push(`${s.name}`);
      lines.push(
        this.t.instant(`${p}.lineSetup`, {
          learners: this.format(s.learners, 'int'),
          platform: this.format(r.platformPerLearnerEff, 'eur3'),
          included: this.format(s.includedPerLearner, 'num'),
          credits: this.format(r.managed.includedCredits, 'int'),
          price: s.creditTiers ? this.t.instant(`${p}.tiers`) : this.format(s.creditPrice, 'eur3'),
        }),
      );
      lines.push(
        this.t.instant(`${p}.lineManaged`, {
          month: this.format(r.managed.baseFee, 'eur'),
          year: this.format(r.managed.baseFee * 12, 'eur'),
          expected: this.format(r.managed.annual, 'eur'),
        }),
      );
      lines.push(
        this.t.instant(`${p}.lineByok`, {
          month: this.format(r.byok.baseFee, 'eur'),
          year: this.format(r.byok.baseFee * 12, 'eur'),
          ops: this.format(s.byokIncludedOps, 'int'),
          over: this.format(s.byokOverPer1000, 'eur3'),
        }),
      );
      if (s.slaLevel !== 'none') {
        lines.push(
          this.t.instant(`${p}.lineSla`, {
            level: this.t.instant(`pages.admin.pricing.sla.levels.${s.slaLevel}.name`),
            flat: this.format(s.slaFlatMonthly, 'eur'),
            hours: this.format(s.slaIncludedHours, 'num'),
            rate: this.format(s.slaHourlyRate, 'eur'),
            info: this.slaInfo(s.slaLevel),
          }),
        );
      }
      if (s.setupFee || s.pilotFee) {
        lines.push(
          this.t.instant(`${p}.lineOneOff`, {
            setup: this.format(s.setupFee, 'eur'),
            pilot: this.format(s.pilotFee, 'eur'),
          }),
        );
      }
      lines.push('');
    }
    lines.push(this.t.instant(`${p}.net`));
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.toastr.success(this.t.instant(`${p}.copied`));
    } catch {
      this.toastr.danger(this.t.instant(`${p}.copyFailed`));
    }
  }

  downloadCsv(): void {
    const p = 'pages.admin.pricing';
    const esc = (v: string) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const num = (v: number) => (Number.isFinite(v) ? String(Math.round(v * 1000) / 1000).replace('.', ',') : '');
    const rows: string[][] = [
      ['', ...this.scenarios.map((s) => s.name)],
      [this.t.instant(`${p}.offers.columns.customer`), ...this.scenarios.map((s) => this.meta[s.id]?.customer ?? '')],
      [
        this.t.instant(`${p}.offers.columns.status`),
        ...this.scenarios.map((s) => {
          const m = this.meta[s.id];
          return m ? this.t.instant(`${p}.offers.status.${m.status}`) : '';
        }),
      ],
      [this.t.instant(`${p}.offers.columns.reason`), ...this.scenarios.map((s) => this.meta[s.id]?.status_reason ?? '')],
    ];
    for (const g of this.inputGroups) {
      for (const r of g.rows) {
        rows.push([
          this.t.instant(`${p}.fields.${r.key}`),
          ...this.scenarios.map((s) => {
            const v = s[r.key];
            return typeof v === 'number' ? num(v) : String(v);
          }),
        ]);
      }
    }
    for (const v of this.variants) {
      for (const r of this.resultRows) {
        rows.push([
          `${this.t.instant(`${p}.variants.${v}`)}: ${this.t.instant(`${p}.results.${r.key}`)}`,
          ...this.scenarios.map((s) => num(this.results[s.id]?.[v][r.key] ?? NaN)),
        ]);
      }
    }
    for (const c of USAGE_CASES) {
      for (const v of this.variants) {
        for (const key of ['monthly', 'contributionMonthly', 'marginPct'] as const) {
          rows.push([
            `${this.t.instant(`${p}.usage.cases.${c}`)} · ${this.t.instant(`${p}.variants.${v}`)}: ${this.t.instant(`${p}.results.${key}`)}`,
            ...this.scenarios.map((s) => num(this.caseResults[s.id]?.[c][v][key] ?? NaN)),
          ]);
        }
      }
    }
    const csv = '﻿' + rows.map((r) => r.map(esc).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `angebot-szenarien-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Altbestand ──────────────────────────────────────────────────────

  /** Szenarien aus dem localStorage vor dem 28.10.2026 (nur zum einmaligen Übernehmen). */
  private readLegacy(): Scenario[] {
    try {
      const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { scenarios?: Partial<Scenario>[] };
      if (!Array.isArray(parsed?.scenarios)) return [];
      return parsed.scenarios.slice(0, MAX_SCENARIOS).map((s) => defaultScenario({ ...s, id: newId() }));
    } catch {
      return [];
    }
  }
}
